// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createActionsHook,
	type Action,
} from '@atlassian/react-sweet-state';

export const CREATE_CHILD_SPOTLIGHT_ID = 'onboardingGetStartedListViewCreateChildButton';
export const CREATE_FIELD_SPOTLIGHT_ID = 'onboardingGetStartedListViewCreateIssueFieldsButton';

export type SpotlightId = typeof CREATE_CHILD_SPOTLIGHT_ID | typeof CREATE_FIELD_SPOTLIGHT_ID;

type State = {
	projectId: string | null;
	isTourActive: boolean;
	isICCOpenByDefault: boolean;
	previousActiveSpotlight: SpotlightId | null;
	currentActiveSpotlight: SpotlightId | null;
};

const defaultNonTourState: State = {
	projectId: null,
	isTourActive: false,
	isICCOpenByDefault: false,
	previousActiveSpotlight: null,
	currentActiveSpotlight: null,
};

const actions = {
	startTour:
		(projectId: string): Action<State> =>
		({ setState }) =>
			setState({
				projectId,
				isTourActive: true,
				isICCOpenByDefault: true,
			}),
	setActiveSpotlight:
		({ projectId, spotlightId }: { projectId: string; spotlightId: SpotlightId }): Action<State> =>
		({ setState }) => {
			// Make sure to reset other state in case there was a tour that previosly started
			setState({
				...defaultNonTourState,
				projectId,
				currentActiveSpotlight: spotlightId,
			});
		},
	dismissSpotlight:
		(spotlightId: SpotlightId): Action<State> =>
		({ setState, getState }) => {
			const state = getState();

			// If not doing the tour then reset onboarding entirely
			if (!state.isTourActive) {
				return setState(defaultNonTourState);
			}

			// If last onboarding step for a tour, reset everything
			if (spotlightId === CREATE_FIELD_SPOTLIGHT_ID) {
				return setState(defaultNonTourState);
			}

			return setState({
				previousActiveSpotlight: spotlightId,
				currentActiveSpotlight: null,
			});
		},
	onIssueCreated:
		(projectId: string): Action<State> =>
		({ setState, getState }) => {
			const state = getState();

			if (projectId !== state.projectId || !state.isTourActive) {
				return;
			}

			if (state.previousActiveSpotlight === null) {
				return setState({
					currentActiveSpotlight: CREATE_CHILD_SPOTLIGHT_ID,
					isICCOpenByDefault: false,
				});
			}

			if (state.previousActiveSpotlight === CREATE_CHILD_SPOTLIGHT_ID) {
				return setState({
					currentActiveSpotlight: CREATE_FIELD_SPOTLIGHT_ID,
				});
			}
		},
};

const Store = createStore<State, typeof actions>({
	initialState: defaultNonTourState,
	actions,
	name: 'GetStartedListViewTour',
});

export const useOnboardingGetStartedTourActions = createActionsHook(Store);

export const useIsIccOpenByDefault = createHook(Store, {
	selector: (state: State, projectId: string) =>
		state.isICCOpenByDefault && state.projectId === projectId,
});

export const useListViewOnboarding = createHook(Store, {
	selector: (state: State, { projectId, id }: { projectId: string; id: string }) =>
		state.currentActiveSpotlight === id && state.projectId === projectId,
});
